import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import TabbedViewWithDateSelect from '../../components/TabbedViewWithDateSelect/TabbedViewWithDateSelect';
import { useAjaxCallbackWithIntervals } from '../../hooks';
import { DayWiseAgsn } from '../../pages';
import moment from 'moment';
import { API } from '../../apis/api';
import {
  AGGREGATE_STATUS,
  API_RESOURCE_URLS,
  DATE_FORMAT,
  MESSAGE_TYPE,
  RESOURCE_TYPE,
} from '../../constants';
import { buildErrorMessage } from '../../apis/calls';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import AggregateLineOut from '../../components/AggregateLineOut/AggregateLineOut';
import { withAllowedOperationsProvider } from '../../hocs';
import { compareByDateAndSerialNumber } from '../../utils/tableUtils';
import { statusStringMap } from '../../staticData';
import { getDatesForRange } from '../../utils/utils';
import { useRef } from 'react';

const DayWiseAgsnView = ({ aggregate }) => {
  const [agsnCount, setAgsnCount] = useState(0);
  const [lineOutCount, setLineOutCount] = useState(0);
  const today = moment();
  const title = 'Line Up';
  const [lastAgsnGenerationDate, setLastAgsnGenerationDate] = useState(null);
  const [hasDayWiseAciveEdit, setHasDayWiseAciveEdit] = useState(false);
  const [hasLineOutActiveEdit, setHasLineOutActiveEdit] = useState(false);
  const { showInternalError, showFailure, showError } = usePopupManager();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateForLineOut, setSelectedDateForLineOut] = useState(null);
  const [hasPendingBacklogAsns, setHasPendingBacklogAsns] = useState(false);
  const [agsnDates, setAgsnDates] = useState([]);
  const [plant] = usePlantAndLinesStore().plantState;
  const [aggregateLine] = usePlantAndLinesStore().aggregateLineState;
  const { showPopup } = usePopupManager();
  const [rowsData, setRowsData] = useState({
    loading: false,
    data: [],
    filteredData: [],
  });
  const alternateMaterialRef = useRef([]);
  const [alternatePart, setAlternatePart] = useState({ data: [] });
  const [lineOutRowsData, setLineOutRowsData] = useState({
    loading: false,
    data: [],
    filteredData: [],
  });

  const loadUsableLastAgsnGenerationDate = async (plantId, lineId) => {
    try {
      const response = await API.get(
        API_RESOURCE_URLS.lastAgSNGeneratedDate(plantId, lineId),
      );
      const { data } = response;
      const lastAgsnGenerationDateFromResponse = data.date;
      if (
        lastAgsnGenerationDateFromResponse &&
        moment(lastAgsnGenerationDateFromResponse).isSameOrAfter(today, 'day')
      ) {
        setLastAgsnGenerationDate(moment(lastAgsnGenerationDateFromResponse));
      } else {
        setLastAgsnGenerationDate(null);
      }
    } catch (error) {
      console.error(buildErrorMessage(error));
      showError({
        contextText: `Failed to Fetch Last AGSN Date`,
        error: error,
      });
    }
  };

  const fetchAgsnsForDate = (dateString, plantId, lineId) =>
    API.get(API_RESOURCE_URLS.agsn(plantId, lineId), {
      params: {
        date: dateString,
        status: [
          AGGREGATE_STATUS.ASN_GENERATED,
          AGGREGATE_STATUS.READY_FOR_DROP,
          AGGREGATE_STATUS.DROPPED,
        ],
      },
    });

  const fetchBacklogAgsnsBeforeDate = (date, plantId, lineId) =>
    API.get(API_RESOURCE_URLS.agsn(plantId, lineId), {
      params: {
        before: date.format(DATE_FORMAT.ISO),
        status: [
          AGGREGATE_STATUS.ASN_GENERATED,
          AGGREGATE_STATUS.READY_FOR_DROP,
          AGGREGATE_STATUS.DROPPED,
        ],
      },
    });

  const fetchAgsnsBasedOnDate = (date, selectedPlant, selectedChassisLine) => {
    const today = moment();
    const dateString = date.format(DATE_FORMAT.ISO);
    if (date.isBefore(today, 'day'))
      return fetchBacklogAgsnsBeforeDate(
        today,
        selectedPlant,
        selectedChassisLine,
      );
    return fetchAgsnsForDate(dateString, selectedPlant, selectedChassisLine);
  };
  const loadAgsnsForADate = async (plantId, lineId, date) => {
    if (!plantId) return;
    if (!lineId) return;
    if (!date) return;
    try {
      await setRowsData(prev => ({ ...prev, loading: true }));
      const response = await fetchAgsnsBasedOnDate(date, plantId, lineId);
      const { data: existingAgsns } = response;
      const filteredAgsns = existingAgsns.map(item => ({
        ...item,
        materialGroupClusters: [],
        aggregateStatus: statusStringMap[item.status],
        aggregateShift: item.shiftName,
        colour: item?.attributes?.colourDescription
          ? `${item?.attributes?.colourDescription} (${item?.attributes?.colourCode})`
          : null,
        country: item?.attributes?.countryCode
          ? `${item?.attributes?.countryCode} (${item?.attributes?.country})`
          : null,
        flashingFlag: item?.attributes?.flashingFlag ? 'Completed' : 'Pending',
      }));
      filteredAgsns.sort((a, b) =>
        compareByDateAndSerialNumber(a, b, 'productionDate', 'sequence'),
      );
      await setRowsData({
        data: filteredAgsns,
        filteredData: filteredAgsns,
        loading: false,
      });
      setAgsnCount(filteredAgsns.length);
    } catch (error) {
      if (error?.response?.status === 400) {
        showFailure({});
      } else {
        showInternalError(error);
        console.error(buildErrorMessage(error));
      }
      await setRowsData(prev => ({
        data: prev.data,
        filteredData: prev.filteredData,
        loading: false,
      }));
    }
  };

  let alternatePartResponse = [];

  const alternatePartAPICall = async partNumberList => {
    if (partNumberList.data.length > 0) {
      const alternateMaterialPayload = {
        materialNumbers: Array.from(
          new Set(partNumberList?.data?.map(row => row.materialNumber)),
        ),
      };
      try {
        alternatePartResponse = await API.post(
          API_RESOURCE_URLS.getAlternatePart(plant.value),
          alternateMaterialPayload,
        );
        setAlternatePart(alternatePartResponse);
        return alternatePartResponse;
      } catch (err) {
        console.error(buildErrorMessage(err));
      }
    }
  };

  const fetchAllLineOutAgsns = (plantId, lineId) =>
    API.get(API_RESOURCE_URLS.linedOutAggregates(plantId, lineId));

  const fetchLineOutAgsnsForDate = (dateString, plantId, lineId) =>
    API.get(API_RESOURCE_URLS.linedOutAggregates(plantId, lineId), {
      params: {
        date: dateString,
      },
    });

  const fetchLineoutAgsnsBasedOnDate = (
    date,
    selectedPlant,
    selectedChassisLine,
  ) => {
    const today = moment();
    const dateString = date.format(DATE_FORMAT.ISO);
    if (date.isBefore(today, 'day'))
      return fetchAllLineOutAgsns(selectedPlant, selectedChassisLine);
    return fetchLineOutAgsnsForDate(
      dateString,
      selectedPlant,
      selectedChassisLine,
    );
  };

  const loadLinedOutAgsnOfSelectedPlantAndLineForDate = async (
    plantId,
    lineId,
    date,
  ) => {
    if (!plantId) return;
    if (!lineId) return;
    if (!date) return;

    try {
      setLineOutRowsData(prevState => ({ ...prevState, loading: true }));

      const response = await fetchLineoutAgsnsBasedOnDate(
        date,
        plantId,
        lineId,
      );
      const dataSortedByProductionDate = response.data.sort(
        (a, b) =>
          moment(a.originalProductionDate) - moment(b.originalProductionDate),
      );
      const lineOutAgsnsWithStatusMapping = dataSortedByProductionDate.map(
        item => ({
          ...item,
          aggregateStatus: statusStringMap[item.status],
          aggregateShift: item.shiftName,
        }),
      );

      setLineOutRowsData({
        loading: false,
        data: lineOutAgsnsWithStatusMapping,
        filteredData: lineOutAgsnsWithStatusMapping,
      });

      setLineOutCount(lineOutAgsnsWithStatusMapping.length);
    } catch (err) {
      setLineOutRowsData({
        loading: false,
        data: [],
        filteredData: [],
      });
      console.error(buildErrorMessage(err));
      showPopup({ type: MESSAGE_TYPE.INTERNAL_ERROR, error: err });
      await setRowsData(prevState => ({ ...prevState, loading: false }));
    }
  };

  const getPendingBacklogAsnsBeforeDate = async (date, plantId, lineId) => {
    const { data } = await API.get(API_RESOURCE_URLS.agsn(plantId, lineId), {
      params: {
        before: date.format(DATE_FORMAT.ISO),
        status: [AGGREGATE_STATUS.ASN_GENERATED],
      },
    });

    return data;
  };

  const setPreviousPendingAsnsCheck = async (
    date,
    selectedPlant,
    selectedChassisLine,
  ) => {
    if (!date || date.isBefore(today, 'day'))
      return setHasPendingBacklogAsns(false);
    try {
      const plannedAgsns = await getPendingBacklogAsnsBeforeDate(
        date,
        selectedPlant,
        selectedChassisLine,
      );

      return setHasPendingBacklogAsns(plannedAgsns.length > 0);
    } catch (err) {
      console.error(buildErrorMessage(err));
      showInternalError({ error: err });
    }
  };

  const checkForPendingAgsnAndLoadData = async (plantId, lineId, date) => {
    if (!plantId) return;
    if (!lineId) return;
    if (!date) return;

    await setPreviousPendingAsnsCheck(moment(date), plantId, lineId);

    await loadAgsnsForADate(plantId, lineId, date);
  };
  const handleReloadData = async () => {
    if (plant.value && aggregateLine.value) {
      checkForPendingAgsnAndLoadData(
        plant.value,
        aggregateLine.value,
        selectedDate,
      );
      loadLinedOutAgsnOfSelectedPlantAndLineForDate(
        plant.value,
        aggregateLine.value,
        selectedDateForLineOut,
      );
      await loadUsableLastAgsnGenerationDate(plant.value, aggregateLine.value);
    }
  };

  const lastRefreshedDate = useAjaxCallbackWithIntervals(
    rowsData.loading,
    hasDayWiseAciveEdit,
    checkForPendingAgsnAndLoadData,
    plant?.value,
    aggregateLine?.value,
    selectedDate,
  );
  const lastRefreshedDateForLineOut = useAjaxCallbackWithIntervals(
    lineOutRowsData.loading,
    hasLineOutActiveEdit,
    loadLinedOutAgsnOfSelectedPlantAndLineForDate,
    plant?.value,
    aggregateLine?.value,
    selectedDateForLineOut,
  );

  useEffect(() => {
    if (lastAgsnGenerationDate) {
      setSelectedDate(moment());
      setSelectedDateForLineOut(moment().subtract(1, 'days'));
      setAgsnDates(getDatesForRange(moment(), lastAgsnGenerationDate));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastAgsnGenerationDate?.format(DATE_FORMAT.ISO)]);

  useEffect(() => {
    if (plant?.value && aggregateLine?.value)
      loadUsableLastAgsnGenerationDate(plant.value, aggregateLine.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant, aggregateLine]);
  return (
    <div>
      <TabbedViewWithDateSelect
        tabs={[
          {
            label: 'AgSN Details',
            title: 'AgSN Details',
            count: agsnCount,
            component: DayWiseAgsn,
            props: {
              setAgsnCount,
              aggregate,
              rowsData,
              alternatePartResponse: { alternatePart },
              setHasDayWiseAciveEdit,
              loadAgsnsForADate,
              alternatePartAPICall,
              loadLinedOutAgsnOfSelectedPlantAndLineForDate,
              lastAgsnGenerationDate,
              lastRefreshedDate,
              selectedDate,
              setSelectedDate,
              hasPendingBacklogAsns,
              agsnDates,
            },
          },
          {
            label: 'Lineout',
            title: 'Lineout Update',
            count: lineOutCount,
            countColor: 'red',
            component: AggregateLineOut,
            props: {
              setLineOutCount,
              aggregate,
              rowsData: lineOutRowsData,
              lastRefreshedDate: lastRefreshedDateForLineOut,
              setHasLineOutActiveEdit,
              loadLinedOutAgsnOfSelectedPlantAndLineForDate,
              lastAgsnGenerationDate,
              loadAgsnsForADate,
              selectedDate: selectedDateForLineOut,
              setSelectedDate: setSelectedDateForLineOut,
              agsnDates,
            },
          },
        ]}
        pageTitle={title}
        reloadData={handleReloadData}
      />
    </div>
  );
};

DayWiseAgsnView.propTypes = {
  aggregate: PropTypes.string.isRequired,
};

export default withAllowedOperationsProvider(
  DayWiseAgsnView,
  true,
  RESOURCE_TYPE.MATERIAL,
);
